import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { X as CloseIcon, Plus as AddIcon } from 'lucide-react';
import { Customer } from '@/utils/localStorage';
import { updateCustomer } from '@/utils/localStorage';

interface CustomerEditFormProps {
  customer: Customer;
  open: boolean;
  onClose: () => void;
  onSave: (customer: Customer) => void;
}

// 拡張されたCustomer型（Phase 2準備）
interface ExtendedCustomerForm extends Customer {
  nameKana?: string;
  birthDate?: string;
  gender?: 'male' | 'female' | 'other' | 'unspecified';
  firstVisitDate?: string;
  bookingSource?: string;
  memo?: string;
  address?: string;
  phoneSecondary?: string;
}

export const CustomerEditForm: React.FC<CustomerEditFormProps> = ({
  customer,
  open,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<ExtendedCustomerForm>({
    ...customer,
    nameKana: customer.nameKana || '',
    birthDate: customer.birthDate || '',
    gender: customer.gender || 'unspecified',
    firstVisitDate: customer.firstVisitDate || customer.createdAt,
    bookingSource: customer.bookingSource || '',
    memo: customer.memo || '',
    address: customer.address || '',
    phoneSecondary: customer.phoneSecondary || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (open && customer) {
      setFormData({
        ...customer,
        nameKana: customer.nameKana || '',
        birthDate: customer.birthDate || '',
        gender: customer.gender || 'unspecified',
        firstVisitDate: customer.firstVisitDate || customer.createdAt,
        bookingSource: customer.bookingSource || '',
        memo: customer.memo || '',
        address: customer.address || '',
        phoneSecondary: customer.phoneSecondary || ''
      });
      setErrors({});
      setNewTag('');
    }
  }, [open, customer]);

  const handleInputChange = (field: keyof ExtendedCustomerForm) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (field: keyof ExtendedCustomerForm) => (value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags?.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = '氏名は必須です';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = '電話番号は必須です';
    } else if (!/^[0-9-]+$/.test(formData.phone)) {
      newErrors.phone = '電話番号は数字とハイフンのみ入力可能です';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }

    if (formData.phoneSecondary && !/^[0-9-]*$/.test(formData.phoneSecondary)) {
      newErrors.phoneSecondary = '電話番号は数字とハイフンのみ入力可能です';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      try {
        // localStorageを更新
        const updatedCustomer = updateCustomer(customer.id, formData);
        if (updatedCustomer) {
          onSave(updatedCustomer);
        }
      } catch (error) {
        console.error('Failed to update customer:', error);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>顧客情報編集</span>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <CloseIcon className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">基本情報</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  氏名 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  className={errors.name ? 'border-destructive' : ''}
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="nameKana">氏名（カナ）</Label>
                <Input
                  id="nameKana"
                  value={formData.nameKana}
                  onChange={handleInputChange('nameKana')}
                  placeholder="ヤマダ タロウ"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">
                  電話番号 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  placeholder="090-1234-5678"
                  className={errors.phone ? 'border-destructive' : ''}
                />
                {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phoneSecondary">電話番号2</Label>
                <Input
                  id="phoneSecondary"
                  value={formData.phoneSecondary}
                  onChange={handleInputChange('phoneSecondary')}
                  placeholder="03-1234-5678"
                  className={errors.phoneSecondary ? 'border-destructive' : ''}
                />
                {errors.phoneSecondary && <p className="text-sm text-destructive">{errors.phoneSecondary}</p>}
              </div>

              <div className="col-span-full space-y-2">
                <Label htmlFor="email">メールアドレス</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={handleInputChange('email')}
                  placeholder="example@email.com"
                  className={errors.email ? 'border-destructive' : ''}
                />
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>

              <div className="col-span-full space-y-2">
                <Label htmlFor="address">住所</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={handleInputChange('address')}
                  placeholder="東京都渋谷区..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="text-sm font-semibold">詳細情報</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="birthDate">誕生日</Label>
                <Input
                  id="birthDate"
                  type="date"
                  value={formData.birthDate}
                  onChange={handleInputChange('birthDate')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gender">性別</Label>
                <Select value={formData.gender} onValueChange={handleSelectChange('gender')}>
                  <SelectTrigger id="gender">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unspecified">未指定</SelectItem>
                    <SelectItem value="male">男性</SelectItem>
                    <SelectItem value="female">女性</SelectItem>
                    <SelectItem value="other">その他</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="firstVisitDate">初回来店日</Label>
                <Input
                  id="firstVisitDate"
                  type="date"
                  value={formData.firstVisitDate?.split('T')[0] || ''}
                  onChange={handleInputChange('firstVisitDate')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bookingSource">予約経路</Label>
                <Select value={formData.bookingSource} onValueChange={handleSelectChange('bookingSource')}>
                  <SelectTrigger id="bookingSource">
                    <SelectValue placeholder="未設定" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">未設定</SelectItem>
                    <SelectItem value="hotpepper">ホットペッパービューティー</SelectItem>
                    <SelectItem value="rakuten">楽天ビューティー</SelectItem>
                    <SelectItem value="google">Google予約</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="referral">紹介</SelectItem>
                    <SelectItem value="walk-in">飛び込み</SelectItem>
                    <SelectItem value="other">その他</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-full space-y-2">
                <Label htmlFor="memo">顧客メモ</Label>
                <Textarea
                  id="memo"
                  value={formData.memo}
                  onChange={handleInputChange('memo')}
                  placeholder="施術の好み、注意事項など..."
                  rows={3}
                />
              </div>

              <div className="col-span-full space-y-2">
                <Label>タグ</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tags?.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => handleRemoveTag(tag)}
                    >
                      {tag}
                      <CloseIcon className="ml-1 h-3 w-3" />
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="新しいタグを追加"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="default"
                    onClick={handleAddTag}
                  >
                    <AddIcon className="mr-2 h-4 w-4" />
                    追加
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose} variant="outline">
            キャンセル
          </Button>
          <Button onClick={handleSubmit} variant="default">
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
